import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, FileText, CheckSquare, Clock, Upload, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useTranslation(user?.language || 'ja');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { key: 'totalContacts', icon: Users, color: 'text-primary', bgColor: 'bg-primary/10', value: stats?.total_contacts || 0, link: '/contacts' },
    { key: 'totalFiles', icon: FileText, color: 'text-green-400', bgColor: 'bg-green-500/10', value: stats?.total_files || 0, link: '/files' },
    { key: 'totalTasks', icon: CheckSquare, color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', value: stats?.total_tasks || 0, link: '/tasks' },
    { key: 'pendingTasks', icon: Clock, color: 'text-destructive', bgColor: 'bg-destructive/10', value: stats?.pending_tasks || 0, link: '/tasks' },
    { key: 'filesToday', icon: Upload, color: 'text-chart-2', bgColor: 'bg-chart-2/10', value: stats?.files_today || 0, link: '/files' },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'file_upload':
      case 'batch_upload':
        return <Upload className="w-4 h-4 text-green-400" />;
      case 'contact_create':
      case 'contact_update':
        return <Users className="w-4 h-4 text-primary" />;
      case 'task_create':
      case 'task_update':
        return <CheckSquare className="w-4 h-4 text-yellow-400" />;
      default:
        return <Activity className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(user?.language === 'en' ? 'en-US' : 'ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t.common.loading}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-glow">{t.dashboard.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {user?.language === 'ja' ? `ようこそ、${user?.name}さん` : `Welcome, ${user?.name}`}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.key} 
            className="card-cyber cursor-pointer hover:border-primary/50 transition-colors" 
            data-testid={`stat-${stat.key}`}
            onClick={() => navigate(stat.link)}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className={`p-1.5 sm:p-2 rounded-sm ${stat.bgColor}`}>
                  <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.color}`} />
                </div>
                <span className="text-xl sm:text-2xl font-bold font-mono">{stat.value}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 sm:mt-2">{t.dashboard[stat.key]}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <Card className="card-cyber" data-testid="recent-activity">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            {t.dashboard.recentActivity}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_activities?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_activities.map((activity, idx) => (
                <div
                  key={activity.id || idx}
                  className="flex items-start gap-3 p-3 rounded-sm bg-secondary/50 hover:bg-secondary transition-colors"
                >
                  <div className="mt-0.5">{getActivityIcon(activity.action_type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{activity.description}</p>
                    <p className="text-xs text-muted-foreground font-mono">{formatDate(activity.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>{user?.language === 'ja' ? 'アクティビティはありません' : 'No recent activity'}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
